import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  ListItemText,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";

import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

//API
import PermissionsApi from "api/permissions";
import UsuariosApi from "api/userApi";

// Función para validar el formato básico del email
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// Función para limpiar y validar el input de email en tiempo real
const validateEmailInput = (value) => {
  if (value !== null) {
    // Eliminar espacios en blanco al inicio y final
    let cleanValue = value.trim();
    // Convertir a minúsculas para consistencia
    cleanValue = cleanValue.toLowerCase();
    // Remover caracteres no permitidos en emails
    cleanValue = cleanValue.replace(/[^a-z0-9@._-]/g, "");
    return {
      value: cleanValue,
      isValid: isValidEmail(cleanValue),
      errorMessage: isValidEmail(cleanValue) ? "" : "Por favor ingrese un Email válido",
    };
  }
};

const EditUsuarioModal = ({ open, onClose, usuario, onSuccess }) => {
  const [identificacion, setIdentificacion] = useState("");
  const [nombres, setNombres] = useState("");
  const [apellidos, setApellidos] = useState("");
  //const [firstName, setName] = useState("");
  const [email, setEmail] = useState("");
  //const [password, setPassword] = useState("");
  const [esOperador, setEsOperador] = useState(false);
  const [tarjetaProfesional, setTarjetaProfesional] = useState("");
  const [ciudadDoc, setCiudadDoc] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [fechaNacimiento, setFechaNacimiento] = useState(null);
  const [genero, setGenero] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (usuario) {
      setIdentificacion(usuario.identificacion);
      setNombres(usuario.nombres);
      setApellidos(usuario.apellidos);
      setEmail(usuario.email);
      //setPassword(usuario.password);
      setEsOperador(usuario.es_operador);
      setTarjetaProfesional(usuario.tarjeta_profesional);
      setCiudadDoc(usuario.ciudad_documento);
      setSelectedRoles(usuario.groups_det.map((group) => group.id));
      setFechaNacimiento(usuario.fecha_nacimiento);
      setGenero(usuario.genero);
    } else {
      resetState();
    }
  }, [usuario]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await PermissionsApi.fetchGroups();
        setGrupos(response.data);
      } catch (error) {
        setError(obtenerMensajeError(error, "Error al cargar los grupos"));
      }
    };
    fetchGroups();
  }, []);

  const handleSetEsOperador = (event) => {
    const newEsOperador = event.target.checked; // Tomamos el nuevo valor del Checkbox
    const operadorGroup = grupos.find((g) => g.name === "OPERADOR(A)");
    if (!operadorGroup) return; // Verificamos que exista el grupo OPERADOR(A)
    if (newEsOperador) {
      // Si se activa "Es Operador", añadimos el rol OPERADOR sin eliminar otros
      setSelectedRoles((prevRoles) =>
        prevRoles.includes(operadorGroup.id) ? prevRoles : [...prevRoles, operadorGroup.id]
      );
    } else {
      // Si se desactiva "Es Operador", quitamos el rol OPERADOR sin afectar los demás
      setSelectedRoles((prevRoles) => prevRoles.filter((roleId) => roleId !== operadorGroup.id));
    }
    // Cambiamos el estado de esOperador al nuevo valor
    setEsOperador(newEsOperador);
  };

  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedRoles(typeof value === "string" ? value.split(",") : value);
  };

  const hcGenero = (event) => {
    setGenero(event.target.value);
  };

  const handleFechaNacimientoChange = (newValue) => {
    if (newValue) {
      const year = newValue.$y;
      const month = String(newValue.$M + 1).padStart(2, "0");
      const day = String(newValue.$D).padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;
      setFechaNacimiento(fecha);
    }
  };

  const resetState = () => {
    setIdentificacion("");
    setNombres("");
    setApellidos("");
    setEmail("");
    //setPassword("");
    setEsOperador(false);
    setTarjetaProfesional("");
    setCiudadDoc("");
    setSelectedRoles([]);
    setFechaNacimiento(null);
    setGenero("");
    setError(undefined);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (nombres === "" || apellidos === "" || ciudadDoc === "") {
      setError("Debes registrar un Nombres, Apellidos y Ciudad de Expedición de la Cédula");
      return;
    }
    const result = validateEmailInput(email);
    if (result?.errorMessage) {
      setError(result.errorMessage);
      return;
    }
    if (fechaNacimiento == null) {
      setError("Debes registrar una Fecha de Nacimiento.");
      return;
    }
    try {
      let userData = {
        identificacion: identificacion,
        email: email,
        //password: password,
        nombres: nombres,
        apellidos: apellidos,
        ciudad_documento: ciudadDoc,
        es_operador: esOperador,
        grupos: selectedRoles,
        groups: selectedRoles,
        fecha_nacimiento: fechaNacimiento,
        genero: genero,
      };
      if (esOperador) {
        userData.tarjeta_profesional = tarjetaProfesional;
      }
      if (usuario) {
        await UsuariosApi.updateUser(usuario.id, userData);
      }
      resetState();
      onSuccess();
    } catch (error) {
      console.error("Error al guardar los datos del Usuario:", error);
      setError(obtenerMensajeError(error, "Error al guardar los datos del Usuario."));
    }
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
      <DialogTitle>{usuario ? "Editar Usuario" : "No hay Usuario."}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Identificación
              </SuiTypography>
              <SuiInput
                value={identificacion}
                onChange={(event) => {
                  const onlyNums = event.target.value.replace(/[^0-9]/g, "");
                  setIdentificacion(onlyNums);
                  setError(undefined);
                }}
                type="text"
                inputProps={{
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  style: { WebkitAppearance: "none", MozAppearance: "textfield" },
                }}
              />
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Ciudad Expedición
              </SuiTypography>
              <SuiInput
                value={ciudadDoc}
                onChange={(event) => {
                  setCiudadDoc(event.target.value);
                  setError(undefined);
                }}
              />
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Email
              </SuiTypography>
              <SuiInput
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError(undefined);
                }}
                type="email"
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombres
              </SuiTypography>
              <SuiInput
                value={nombres}
                onChange={(event) => {
                  setNombres(event.target.value);
                  setError(undefined);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Apellidos
              </SuiTypography>
              <SuiInput
                value={apellidos}
                onChange={(event) => {
                  setApellidos(event.target.value);
                  setError(undefined);
                }}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={8}>
              <SuiTypography variant="h6" fontWeight="light">
                Roles
              </SuiTypography>
              <Select
                labelId="multiple-checkbox-label"
                id="multiple-checkbox"
                multiple
                value={selectedRoles}
                onChange={handleRoleChange}
                input={<OutlinedInput label="Roles" sx={{ height: "41px" }} />}
                renderValue={(selected) =>
                  grupos
                    .filter((g) => selected.includes(g.id))
                    .map((g) => g.name)
                    .join(", ")
                }
                sx={{
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.875rem",
                    minHeight: "1.4375em",
                    padding: "0.5rem",
                  },
                }}
              >
                {grupos.map((grupo) => (
                  <MenuItem
                    key={grupo.id}
                    value={grupo.id}
                    sx={{
                      padding: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      checked={selectedRoles.indexOf(grupo.id) > -1}
                      sx={{
                        padding: 0,
                        marginRight: "0.5rem",
                      }}
                    />
                    <ListItemText
                      primary={grupo.name}
                      primaryTypographyProps={{
                        style: {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {/* <Grid size={6}>
              <InputLabel>Contraseña</InputLabel>
              <SuiInput
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError(undefined);
                }}
                type="password"
              />
            </Grid> */}
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Fecha de Nacimiento
              </SuiTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fechaNacimiento ? dayjs(fechaNacimiento) : null}
                  onChange={handleFechaNacimientoChange}
                  format="DD/MM/YYYY"
                  fullWidth
                  renderInput={() => (
                    <SuiInput
                      placeholder="Fecha de Nacimiento"
                      icon={{
                        component: <CalendarTodayIcon />,
                        direction: "right",
                      }}
                      customClass="custom-date-picker-input"
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Género
              </SuiTypography>
              <Select
                labelId="genero-label"
                id="genero-select"
                value={genero}
                label="Género"
                onChange={hcGenero}
                disabled={false}
              >
                <MenuItem value="FE">Femenino</MenuItem>
                <MenuItem value="MA">Masculino</MenuItem>
                <MenuItem value="NR">No Responde</MenuItem>
              </Select>
            </Grid>
            <Grid size={4} sx={{ display: "flex", alignItems: "center" }}>
              {/* <SuiTypography variant="h6" fontWeight="light">
                ¿Es Operador?
              </SuiTypography> */}
              <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={esOperador} onChange={handleSetEsOperador} />
                <SuiTypography variant="h6" fontWeight="light">
                  ¿Es Operador?
                </SuiTypography>
              </InputLabel>
            </Grid>
            {esOperador && (
              <Grid size={4}>
                <SuiTypography variant="h6" fontWeight="light">
                  Tarjeta Profesional
                </SuiTypography>
                <SuiInput
                  value={tarjetaProfesional}
                  onChange={(event) => {
                    setTarjetaProfesional(event.target.value);
                    setError(undefined);
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            {usuario ? "Actualizar" : "Crear"}
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

EditUsuarioModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  usuario: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditUsuarioModal;
